import React, { useState, useEffect } from 'react';
import { Settings, Bell, Zap, Eye, Save, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Configuration = ({ project }) => {
  const [projectConfig, setProjectConfig] = useState(null);
  const [systemConfig, setSystemConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchConfigurations();
  }, [project.id]);

  const fetchConfigurations = async () => {
    try {
      const [projectRes, systemRes] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/config`),
        axios.get(`${API}/config/system`)
      ]);
      setProjectConfig(projectRes.data);
      setSystemConfig(systemRes.data);
    } catch (error) {
      console.error('Error fetching configurations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${project.id}/config`, projectConfig);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category, key, value) => {
    setProjectConfig({
      ...projectConfig,
      [category]: {
        ...projectConfig[category],
        [key]: value
      }
    });
  };

  if (loading || !projectConfig || !systemConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-700 to-gray-900 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="w-10 h-10" />
            <div>
              <h2 className="text-3xl font-bold">Configuration</h2>
              <p className="text-gray-300 mt-1">Customize your project settings and preferences</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || saved}
            className="px-6 py-3 bg-white text-gray-900 rounded-xl hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 flex items-center space-x-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : saved ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Saved!</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* System Info */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-6">System Information</h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Version</p>
            <p className="text-lg font-bold text-gray-900">{systemConfig.version}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Environment</p>
            <p className="text-lg font-bold text-gray-900 capitalize">{systemConfig.environment}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">AI Model</p>
            <p className="text-lg font-bold text-gray-900">{systemConfig.ai_settings.default_model}</p>
          </div>
        </div>
      </div>

      {/* Analysis Settings */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="flex items-center space-x-3 mb-6">
          <Zap className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-bold text-gray-900">Analysis Settings</h3>
        </div>
        <div className="space-y-4">
          <ToggleSetting
            label="Auto Analyze Code"
            description="Automatically run AI analysis when new code is uploaded"
            value={projectConfig.analysis_settings.auto_analyze_code}
            onChange={(value) => updateSetting('analysis_settings', 'auto_analyze_code', value)}
          />
          <ToggleSetting
            label="Auto Generate Tests"
            description="Automatically generate test cases based on changes"
            value={projectConfig.analysis_settings.auto_generate_tests}
            onChange={(value) => updateSetting('analysis_settings', 'auto_generate_tests', value)}
          />
          <ToggleSetting
            label="AI Suggestions"
            description="Enable AI-powered remediation suggestions"
            value={projectConfig.analysis_settings.ai_suggestions_enabled}
            onChange={(value) => updateSetting('analysis_settings', 'ai_suggestions_enabled', value)}
          />
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Risk Threshold</label>
            <select
              value={projectConfig.analysis_settings.risk_threshold}
              onChange={(e) => updateSetting('analysis_settings', 'risk_threshold', e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="low">Low - Flag all potential issues</option>
              <option value="medium">Medium - Balanced approach</option>
              <option value="high">High - Only critical issues</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="flex items-center space-x-3 mb-6">
          <Bell className="w-6 h-6 text-purple-600" />
          <h3 className="text-xl font-bold text-gray-900">Notification Settings</h3>
        </div>
        <div className="space-y-4">
          <ToggleSetting
            label="Email Notifications"
            description="Receive email updates for important events"
            value={projectConfig.notification_settings.email_notifications}
            onChange={(value) => updateSetting('notification_settings', 'email_notifications', value)}
          />
          <ToggleSetting
            label="Task Reminders"
            description="Get reminders for upcoming task deadlines"
            value={projectConfig.notification_settings.task_reminders}
            onChange={(value) => updateSetting('notification_settings', 'task_reminders', value)}
          />
          <ToggleSetting
            label="High Priority Alerts"
            description="Immediate alerts for critical priority items"
            value={projectConfig.notification_settings.high_priority_alerts}
            onChange={(value) => updateSetting('notification_settings', 'high_priority_alerts', value)}
          />
        </div>
      </div>

      {/* Workflow Settings */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Workflow Settings</h3>
        <div className="space-y-4">
          <ToggleSetting
            label="Auto Create Tasks"
            description="Automatically create tasks from AI analysis"
            value={projectConfig.workflow_settings.auto_create_tasks}
            onChange={(value) => updateSetting('workflow_settings', 'auto_create_tasks', value)}
          />
          <ToggleSetting
            label="Require Approval"
            description="Require manual approval before syncing to external tools"
            value={projectConfig.workflow_settings.require_approval}
            onChange={(value) => updateSetting('workflow_settings', 'require_approval', value)}
          />
          <ToggleSetting
            label="Auto Sync Integrations"
            description="Automatically sync changes to Jira/Azure/ServiceNow"
            value={projectConfig.workflow_settings.auto_sync_integrations}
            onChange={(value) => updateSetting('workflow_settings', 'auto_sync_integrations', value)}
          />
        </div>
      </div>

      {/* Display Settings */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <div className="flex items-center space-x-3 mb-6">
          <Eye className="w-6 h-6 text-green-600" />
          <h3 className="text-xl font-bold text-gray-900">Display Settings</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Default View</label>
            <select
              value={projectConfig.display_settings.default_view}
              onChange={(e) => updateSetting('display_settings', 'default_view', e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="dashboard">Dashboard</option>
              <option value="code">Code Assistant</option>
              <option value="workplan">Workplan</option>
              <option value="analytics">Analytics</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Items Per Page</label>
            <select
              value={projectConfig.display_settings.items_per_page}
              onChange={(e) => updateSetting('display_settings', 'items_per_page', parseInt(e.target.value))}
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="10">10 items</option>
              <option value="25">25 items</option>
              <option value="50">50 items</option>
              <option value="100">100 items</option>
            </select>
          </div>

          <ToggleSetting
            label="Show Advanced Metrics"
            description="Display detailed technical metrics in dashboards"
            value={projectConfig.display_settings.show_advanced_metrics}
            onChange={(value) => updateSetting('display_settings', 'show_advanced_metrics', value)}
          />
        </div>
      </div>
    </div>
  );
};

const ToggleSetting = ({ label, description, value, onChange }) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
    <div className="flex-1">
      <p className="font-semibold text-gray-900">{label}</p>
      <p className="text-sm text-gray-600 mt-1">{description}</p>
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        value ? 'bg-green-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          value ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);
